import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import {
  panelLimitErrorKey,
  usePanelLimitConfigurations,
  usePatientPanelUsageLedger,
  useUpdatePanelLimitConfiguration,
  type PanelLimitConfiguration,
  type PanelContractLimits as ContractLimits,
} from '@/features/panel-limits/api';
import { isApiError } from '@/lib/api';
import { PatientPanelUsageLedger } from './PatientPanelUsageLedger';
import styles from './PanelLimits.module.css';

const displayCap = (cap: number | null) =>
  cap === null ? 'No cap' : formatRM(cap);

export function ErrorMessage({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const key = panelLimitErrorKey(error);
  const apiError = isApiError(error) ? error : undefined;
  return (
    <ErrorState
      error={error}
      body={key ? t(`panelLimits.errors.${key}`) : apiError?.message}
    />
  );
}

/** Limits and patient remaining balances shown inside the panel contract drawer. */
export function PanelContractLimits({
  panelId,
  canManage,
}: {
  panelId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const configurations = usePanelLimitConfigurations();
  const usage = usePatientPanelUsageLedger({ panelId });
  const configuration = configurations.data?.find(
    (item) => item.panelId === panelId,
  );

  return (
    <div className={styles.stack}>
      <section aria-labelledby="panel-contract-limits-heading">
        <h3 id="panel-contract-limits-heading" className={styles.heading}>
          {t('panelLimits.contractLimits')}
        </h3>
        {configurations.isLoading ? (
          <LoadingState />
        ) : configurations.error ? (
          <ErrorMessage error={configurations.error} />
        ) : configuration ? (
          <PanelLimitEditor
            key={configuration.updatedAt}
            configuration={configuration}
            canManage={canManage}
          />
        ) : (
          <EmptyState
            title={t('panelLimits.noConfigurations')}
            body={t('panelLimits.noConfigurationsBody')}
          />
        )}
      </section>

      <section aria-labelledby="panel-contract-usage-heading">
        <h3 id="panel-contract-usage-heading" className={styles.heading}>
          {t('panelLimits.remainingByPatient')}
        </h3>
        <PatientPanelUsageLedger
          entries={usage.data}
          loading={usage.isLoading}
          error={usage.error}
          onRetry={() => void usage.refetch()}
        />
      </section>
    </div>
  );
}

export function PanelLimitEditor({
  configuration,
  canManage,
}: {
  configuration: PanelLimitConfiguration;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const update = useUpdatePanelLimitConfiguration();
  const initialValues: Record<keyof ContractLimits, string> = {
    consultationCapSen:
      configuration.consultationCapSen === null
        ? ''
        : senToInput(configuration.consultationCapSen),
    testsCapSen:
      configuration.testsCapSen === null
        ? ''
        : senToInput(configuration.testsCapSen),
    proceduresCapSen:
      configuration.proceduresCapSen === null
        ? ''
        : senToInput(configuration.proceduresCapSen),
    annualPatientLimitSen:
      configuration.annualPatientLimitSen === null
        ? ''
        : senToInput(configuration.annualPatientLimitSen),
    warningThresholdPercent: String(configuration.warningThresholdPercent),
  };
  const [values, setValues] = useState(initialValues);
  const [attempted, setAttempted] = useState(false);
  const cap = (key: keyof Omit<ContractLimits, 'warningThresholdPercent'>) =>
    values[key].trim() ? parseRM(values[key]) : null;
  const input: ContractLimits = {
    consultationCapSen: cap('consultationCapSen'),
    testsCapSen: cap('testsCapSen'),
    proceduresCapSen: cap('proceduresCapSen'),
    annualPatientLimitSen: cap('annualPatientLimitSen'),
    warningThresholdPercent: Number(values.warningThresholdPercent),
  };
  const invalid =
    (Object.keys(input) as (keyof ContractLimits)[]).some(
      (key) =>
        key !== 'warningThresholdPercent' &&
        values[key].trim() !== '' &&
        input[key] === null,
    ) ||
    !Number.isInteger(input.warningThresholdPercent) ||
    input.warningThresholdPercent < 1 ||
    input.warningThresholdPercent > 100;
  const set = (key: keyof ContractLimits, value: string) =>
    setValues((current) => ({ ...current, [key]: value }));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (invalid) return;
    update.mutate(
      { id: configuration.id, ...input },
      {
        onSuccess: () =>
          toast.show({ tone: 'success', title: t('panelLimits.saved') }),
      },
    );
  };

  if (!canManage)
    return (
      <dl className={styles.limitReadout}>
        <dt>{t('panelLimits.consultationCap')}</dt>
        <dd>{displayCap(configuration.consultationCapSen)}</dd>
        <dt>{t('panelLimits.testsCap')}</dt>
        <dd>{displayCap(configuration.testsCapSen)}</dd>
        <dt>{t('panelLimits.proceduresCap')}</dt>
        <dd>{displayCap(configuration.proceduresCapSen)}</dd>
        <dt>{t('panelLimits.annualPatientLimit')}</dt>
        <dd>{displayCap(configuration.annualPatientLimitSen)}</dd>
        <dt>{t('panelLimits.warningThreshold')}</dt>
        <dd>{configuration.warningThresholdPercent}%</dd>
      </dl>
    );

  const fields: {
    key: keyof Omit<ContractLimits, 'warningThresholdPercent'>;
    label: string;
  }[] = [
    { key: 'consultationCapSen', label: t('panelLimits.consultationCapRm') },
    { key: 'testsCapSen', label: t('panelLimits.testsCapRm') },
    { key: 'proceduresCapSen', label: t('panelLimits.proceduresCapRm') },
    {
      key: 'annualPatientLimitSen',
      label: t('panelLimits.annualPatientLimitRm'),
    },
  ];

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.fieldGrid}>
        {fields.map((field) => (
          <Field
            key={field.key}
            label={field.label}
            hint={t('panelLimits.blankForNoCap')}
            error={
              attempted && invalid && field.key === 'consultationCapSen'
                ? t('panelLimits.invalid')
                : undefined
            }
          >
            <Input
              inputMode="decimal"
              value={values[field.key]}
              onChange={(event) => set(field.key, event.target.value)}
            />
          </Field>
        ))}
        <Field
          label={t('panelLimits.warningThresholdPercent')}
          hint={t('panelLimits.warningHint')}
        >
          <Input
            type="number"
            min={1}
            max={100}
            step={1}
            value={values.warningThresholdPercent}
            onChange={(event) =>
              set('warningThresholdPercent', event.target.value)
            }
          />
        </Field>
      </div>
      <ErrorMessage error={update.error} />
      <div className={styles.actions}>
        <Button variant="primary" type="submit" loading={update.isPending}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
